import React, {Component} from 'react';
import {observable, action} from 'mobx';
import {inject, observer} from 'mobx-react';
import {toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from 'moment';
import {
  FormCheckbox, Button, Modal, ModalHeader, ModalBody
} from 'shards-react';
import FeatherIcon from 'feather-icons-react';

import PageTitle from '../../../components/common/PageTitle';
import ModalLoading from '../../../components/common/ModalLoading';
import './style.scss';

@inject(({teamUser}) => ({isLogin: teamUser.isLogin, me: teamUser.me}))
@inject(({review}) => ({
  listReviewCopyJobs: review.listReviewCopyJobs,
  getReviewCopyJob: review.getReviewCopyJob,
  deleteCopiedReviews: review.deleteCopiedReviews
}))
@observer
class ReviewCopyHistory extends Component {

  @observable isLogin = false;
  @observable teamUser = {};
  @observable isLoading = false;

  @observable list = {docs: [], totalDocs: 0, page: 1, totalPages: 1};
  @observable params = {page: 1, limit: 20};

  @observable detail = {
    isOpen: false,
    job: null,
    selectedReviewIds: []
  };

  @observable confirm = {
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  };

  componentDidMount() {
    if (this.props.isLogin && this.props.me) {
      this.isLogin = true;
      this.teamUser = this.props.me;
      this.load();

      // URL ?jobId=... 자동 상세 오픈
      const search = (this.props.location && this.props.location.search) || (typeof window !== 'undefined' ? window.location.search : '');
      const sp = new URLSearchParams(search);
      const jobId = sp.get('jobId');
      if (jobId) {
        this.openDetail(jobId);
      }
    }
  }

  @action load = async () => {
    if (!this.teamUser.selStore_id) return;
    this.isLoading = true;
    try {
      const res = await this.props.listReviewCopyJobs({
        store_id: this.teamUser.selStore_id,
        page: this.params.page,
        limit: this.params.limit
      });
      const data = (res && res.data) ? res.data : {docs: [], totalDocs: 0, totalPages: 1, page: 1};
      this.list = {
        docs: data.docs || [],
        totalDocs: data.totalDocs || 0,
        page: data.page || 1,
        totalPages: data.totalPages || 1
      };
    } catch (e) {
      toast.error('이력 로드 실패');
    } finally {
      this.isLoading = false;
    }
  };

  @action goPage = (p) => {
    if (p < 1 || p > this.list.totalPages) return;
    this.params.page = p;
    this.load();
  };

  @action openDetail = async (jobId) => {
    this.isLoading = true;
    try {
      const res = await this.props.getReviewCopyJob({jobId});
      const item = (res && res.data && res.data.item) ? res.data.item : null;
      this.detail = {isOpen: true, job: item, selectedReviewIds: []};
    } catch (e) {
      toast.error('상세 로드 실패');
    } finally {
      this.isLoading = false;
    }
  };

  @action closeDetail = () => {
    this.detail = {isOpen: false, job: null, selectedReviewIds: []};
  };

  @action toggleDetailSelect = (reviewId) => {
    const idx = this.detail.selectedReviewIds.indexOf(reviewId);
    if (idx >= 0) this.detail.selectedReviewIds.splice(idx, 1);
    else this.detail.selectedReviewIds.push(reviewId);
  };

  @action toggleDetailSelectAll = () => {
    if (!this.detail.job) return;
    const eligible = (this.detail.job.copiedReviews || [])
      .filter(c => c.status === 'ok' && c.newReview_id)
      .map(c => String(c.newReview_id));
    const allSelected = eligible.length > 0 && eligible.every(id => this.detail.selectedReviewIds.includes(id));
    if (allSelected) this.detail.selectedReviewIds = [];
    else this.detail.selectedReviewIds = eligible;
  };

  @action openConfirm = (title, message, onConfirm) => {
    this.confirm = {isOpen: true, title, message, onConfirm};
  };

  @action closeConfirm = () => {
    this.confirm = {isOpen: false, title: '', message: '', onConfirm: null};
  };

  @action runConfirm = async () => {
    const fn = this.confirm.onConfirm;
    this.closeConfirm();
    if (typeof fn === 'function') await fn();
  };

  // 일괄 삭제 (job 전체)
  @action deleteAll = (job) => {
    const okCount = (job.copiedReviews || []).filter(c => c.status === 'ok').length;
    if (!okCount) { toast.warn('삭제 가능한 복사가 없습니다.'); return; }
    this.openConfirm(
      '복사 전체 삭제',
      `이 작업으로 복사된 ${okCount}건의 리뷰를 카페24와 온리플에서 모두 삭제합니다. 계속할까요?`,
      async () => {
        this.isLoading = true;
        try {
          const res = await this.props.deleteCopiedReviews({jobId: String(job._id), store_id: this.teamUser.selStore_id});
          const data = (res && res.data) ? res.data : null;
          if (data && data.error) toast.error('삭제 실패: ' + data.error);
          else toast.success(`삭제 완료 — 성공 ${(data && data.okCount) || 0}, 실패 ${(data && data.failCount) || 0}`);
          await this.load();
          if (this.detail.isOpen && this.detail.job && String(this.detail.job._id) === String(job._id)) {
            await this.openDetail(String(job._id));
          }
        } catch (e) {
          toast.error('삭제 실패');
        } finally {
          this.isLoading = false;
        }
      }
    );
  };

  // 선택 삭제 (상세 모달)
  @action deleteSelected = () => {
    const ids = this.detail.selectedReviewIds.slice();
    if (!ids.length) { toast.warn('삭제할 리뷰를 선택해 주세요.'); return; }
    const job = this.detail.job;
    this.openConfirm(
      '선택 복사 삭제',
      `선택한 ${ids.length}건의 복사 리뷰를 카페24와 온리플에서 삭제합니다. 계속할까요?`,
      async () => {
        this.isLoading = true;
        try {
          const res = await this.props.deleteCopiedReviews({
            jobId: String(job._id),
            store_id: this.teamUser.selStore_id,
            review_ids: ids
          });
          const data = (res && res.data) ? res.data : null;
          if (data && data.error) toast.error('삭제 실패: ' + data.error);
          else toast.success(`삭제 완료 — 성공 ${(data && data.okCount) || 0}, 실패 ${(data && data.failCount) || 0}`);
          await this.openDetail(String(job._id));
          await this.load();
        } catch (e) {
          toast.error('삭제 실패');
        } finally {
          this.isLoading = false;
        }
      }
    );
  };

  renderStatus = (status) => {
    const map = {
      'running': {label: '진행중', cls: 'is-running'},
      'done': {label: '완료', cls: 'is-done'},
      'partial': {label: '부분 실패', cls: 'is-partial'},
      'cancelled': {label: '취소', cls: 'is-cancelled'}
    };
    const m = map[status] || {label: status || '-', cls: ''};
    return <span className={`copy-job-status ${m.cls}`}>{m.label}</span>;
  };

  renderDeletionSummary = (job) => {
    const copies = job.copiedReviews || [];
    const okCopies = copies.filter(c => c.status === 'ok' || c.status === 'deleted').length;
    const deletedCount = copies.filter(c => c.status === 'deleted').length;
    const dels = job.deletions || [];

    if (deletedCount === 0 && dels.length === 0) {
      return <span className="text-muted">—</span>;
    }
    const lastDel = dels.length ? dels[dels.length - 1] : null;
    const isAll = deletedCount > 0 && deletedCount === okCopies;
    const cls = isAll ? 'copy-job-deletion-summary is-all' : 'copy-job-deletion-summary is-partial';
    return (
      <div className={cls}>
        <div className="copy-job-deletion-summary__main">
          {isAll ? '전체 삭제' : `${deletedCount} / ${okCopies}건 삭제`}
        </div>
        {lastDel && (
          <div className="copy-job-deletion-summary__meta">
            {lastDel.deletedAt ? moment(lastDel.deletedAt).format('YY-MM-DD HH:mm') : ''}
            {lastDel.deletedBy ? ` · ${lastDel.deletedBy}` : ''}
            {dels.length > 1 ? ` · 총 ${dels.length}회` : ''}
          </div>
        )}
      </div>
    );
  };

  renderDetailModal() {
    const job = this.detail.job;
    if (!job) return null;
    const copies = job.copiedReviews || [];
    const deletions = job.deletions || [];
    const eligible = copies.filter(c => c.status === 'ok' && c.newReview_id);
    const allSelected = eligible.length > 0 && eligible.every(c => this.detail.selectedReviewIds.includes(String(c.newReview_id)));
    const totalDeleted = copies.filter(c => c.status === 'deleted').length;
    return (
      <Modal size="lg" open={this.detail.isOpen} toggle={this.closeDetail} centered>
        <ModalHeader>복사 작업 상세</ModalHeader>
        <ModalBody className="copy-job-detail-modal-body">
          <div className="copy-job-detail">
            <div className="copy-job-detail__head">
              <div><strong>일시</strong><span className="copy-detail-value">{moment(job.createdAt).format('YYYY-MM-DD HH:mm')}</span></div>
              <div><strong>실행자</strong><span className="copy-detail-value">{job.teamUserName || '-'}</span></div>
              <div title={`${job.sourceProductName || ''} (${job.sourcePlatformProductId})`}>
                <strong>원본</strong><span className="copy-detail-value">{job.sourceProductName || '-'} ({job.sourcePlatformProductId})</span>
              </div>
              <div title={(job.targetProducts || []).map(t => `${t.productName || ''}(${t.platformProductId})`).join(', ')}>
                <strong>대상</strong><span className="copy-detail-value">{(job.targetProducts || []).map(t => `${t.productName || ''}(${t.platformProductId})`).join(', ')}</span>
              </div>
              <div><strong>총건수</strong><span className="copy-detail-value">{job.totalCount} / 성공 {job.successCount} / 실패 {job.failCount}</span></div>
              <div><strong>상태</strong><span className="copy-detail-value">{this.renderStatus(job.status)}</span></div>
            </div>

            {deletions.length > 0 && (
              <div className="copy-job-deletions">
                <div className="copy-job-deletions__title">
                  <FeatherIcon icon="trash-2" size="14"/>
                  <span>삭제 이력 ({deletions.length}회 / 총 {totalDeleted}건 삭제됨)</span>
                </div>
                <table className="copy-job-deletions-table">
                  <thead>
                    <tr><th>삭제일시</th><th>실행자</th><th>범위</th><th>대상</th><th>성공/실패</th></tr>
                  </thead>
                  <tbody>
                    {deletions.map((d, i) => (
                      <tr key={i}>
                        <td>{d.deletedAt ? moment(d.deletedAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                        <td>{d.deletedBy || '-'}</td>
                        <td>{d.scope === 'all' ? '전체 삭제' : '선택 삭제'}</td>
                        <td>{d.attemptCount}건</td>
                        <td>
                          <span className="text-success">{d.okCount}</span>
                          {' / '}
                          <span className="text-danger">{d.failCount}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="copy-job-detail__actions">
              <Button size="sm" theme="light" onClick={() => this.toggleDetailSelectAll()}>
                <FormCheckbox small checked={allSelected} onChange={() => {}} className="d-inline-block mr-1"/>
                전체 선택
              </Button>
              <Button size="sm" theme="danger" className="ml-2"
                      disabled={!this.detail.selectedReviewIds.length}
                      onClick={() => this.deleteSelected()}>
                선택 삭제 ({this.detail.selectedReviewIds.length})
              </Button>
              <Button size="sm" theme="warning" className="ml-2"
                      onClick={() => this.deleteAll(job)}>
                전체 삭제
              </Button>
            </div>

            <table className="s-table copy-job-detail-table">
              <colgroup>
                <col style={{width: '36px'}}/>
                <col style={{width: '110px'}}/>
                <col style={{width: '90px'}}/>
                <col/>
                <col style={{width: '70px'}}/>
              </colgroup>
              <thead>
                <tr><th></th><th>대상 상품</th><th>article_no</th><th>상태/메시지</th><th>리뷰ID</th></tr>
              </thead>
              <tbody>
                {copies.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted">기록 없음</td></tr>
                )}
                {copies.map((c, idx) => {
                  const reviewId = c.newReview_id ? String(c.newReview_id) : null;
                  const canSelect = c.status === 'ok' && reviewId;
                  const checked = reviewId && this.detail.selectedReviewIds.includes(reviewId);
                  const isDeleted = c.status === 'deleted';
                  return (
                    <tr key={idx} className={isDeleted ? 'is-deleted' : ''}>
                      <td>
                        {canSelect && <FormCheckbox small checked={checked} onChange={() => this.toggleDetailSelect(reviewId)}/>}
                      </td>
                      <td>{c.targetPlatformProductId || '-'}</td>
                      <td>{c.platformArticleNo || '-'}</td>
                      <td>
                        <span className={`copy-status-badge copy-status-badge--${c.status}`}>{c.status}</span>
                        {c.note && <span className="copy-status-note">{c.note}</span>}
                        {c.error && <span className="copy-status-error">{c.error}</span>}
                        {isDeleted && (
                          <div className="copy-status-deleted-info">
                            {c.deletedAt && <span>{moment(c.deletedAt).format('YY-MM-DD HH:mm')}</span>}
                            {c.deletedBy && <span> · {c.deletedBy}</span>}
                            {c.cafe24DeleteError && <span className="copy-status-error">카페24: {c.cafe24DeleteError}</span>}
                            {c.deleteDbError && <span className="copy-status-error">DB: {c.deleteDbError}</span>}
                          </div>
                        )}
                      </td>
                      <td className="text-muted">{reviewId ? reviewId.substring(reviewId.length - 6) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="text-center mt-3">
              <Button theme="light" onClick={this.closeDetail}>닫기</Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    );
  }

  render() {
    if (!this.isLogin) {
      return (
        <div className="main-content">
          <PageTitle/>
          <div className="widget-box"><p className="text-center p-4">로그인이 필요합니다.</p></div>
        </div>
      );
    }

    return (
      <div className="main-content">
        <PageTitle/>
        <div className="widget-box review-copy-history">

          <div className="widget-page-tabs">
            <div className="page-tab-header">
              <p>상품 리뷰 복사 작업의 이력을 확인하고 복사된 리뷰를 일괄/선택 삭제할 수 있습니다.</p>
            </div>
          </div>

          <table className="s-table copy-history-table">
            <colgroup>
              <col style={{width: '140px'}}/>
              <col style={{width: '100px'}}/>
              <col style={{width: '200px'}}/>
              <col/>
              <col style={{width: '130px'}}/>
              <col style={{width: '90px'}}/>
              <col style={{width: '160px'}}/>
              <col style={{width: '170px'}}/>
            </colgroup>
            <thead>
              <tr>
                <th>일시</th><th>실행자</th><th>원본</th><th>대상</th>
                <th>총/성공/실패</th><th>상태</th><th>삭제</th><th></th>
              </tr>
            </thead>
            <tbody>
              {this.list.docs.length === 0 && (
                <tr><td colSpan={8} className="text-center text-muted">이력이 없습니다.</td></tr>
              )}
              {this.list.docs.map(job => (
                <tr key={String(job._id)}>
                  <td>{moment(job.createdAt).format('YYYY-MM-DD HH:mm')}</td>
                  <td>{job.teamUserName || '-'}</td>
                  <td>
                    <div>{job.sourceProductName || '-'}</div>
                    <div className="text-muted">{job.sourcePlatformProductId}</div>
                  </td>
                  <td>
                    {(job.targetProducts || []).slice(0, 3).map(t => (
                      <span key={t.platformProductId} className="copy-target-pill">{t.productName || t.platformProductId}</span>
                    ))}
                    {(job.targetProducts || []).length > 3 && <span className="copy-target-pill copy-target-pill--more">+{(job.targetProducts || []).length - 3}</span>}
                  </td>
                  <td>{job.totalCount} / <span className="text-success">{job.successCount}</span> / <span className="text-danger">{job.failCount}</span></td>
                  <td>{this.renderStatus(job.status)}</td>
                  <td>{this.renderDeletionSummary(job)}</td>
                  <td>
                    <Button size="sm" theme="light" onClick={() => this.openDetail(String(job._id))}>상세</Button>
                    <Button size="sm" theme="warning" className="ml-1" onClick={() => this.deleteAll(job)}>전체 삭제</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="copy-history-paging">
            <Button size="sm" theme="light" disabled={this.list.page <= 1} onClick={() => this.goPage(this.list.page - 1)}>이전</Button>
            <span className="mx-2">{this.list.page} / {this.list.totalPages}</span>
            <Button size="sm" theme="light" disabled={this.list.page >= this.list.totalPages} onClick={() => this.goPage(this.list.page + 1)}>다음</Button>
          </div>

        </div>

        {this.renderDetailModal()}

        {/* 확인 모달 */}
        <Modal size="sm" open={this.confirm.isOpen} toggle={this.closeConfirm} centered>
          <ModalHeader>{this.confirm.title}</ModalHeader>
          <ModalBody>
            <p>{this.confirm.message}</p>
            <div className="text-center mt-3">
              <Button theme="light" onClick={this.closeConfirm}>취소</Button>
              <Button theme="danger" className="ml-2" onClick={() => this.runConfirm()}>확인</Button>
            </div>
          </ModalBody>
        </Modal>

        <ModalLoading isLoading={this.isLoading}/>
      </div>
    );
  }
}

export default ReviewCopyHistory;
